import { useCallback, useEffect, useRef, useState } from 'react';
import { calendarPrefKey } from '@lapo/core';
import type { ApiClient, UserCalendarDTO } from './auth/apiClient.js';

/**
 * Owns the "Watched calendars" setting: the list of the user's Google calendars with their resolved
 * on/off state, plus per-calendar toggling. Toggling updates the local list optimistically and
 * debounces a PUT of the full overrides map (the server then re-registers watches so a disabled
 * calendar goes off everywhere). `api` null (signed out) keeps it empty.
 */
const SAVE_DEBOUNCE_MS = 600;

type CalendarApi = Pick<ApiClient, 'getCalendars' | 'getCalendarPrefs' | 'putCalendarPrefs'>;

export interface UseCalendarPrefs {
  calendars: UserCalendarDTO[];
  /** Toggle a single calendar on/off (optimistic + debounced persist). */
  setEnabled: (accountId: string, calendarId: string, enabled: boolean) => void;
  /** Re-fetch the calendar list + prefs — call after linking/unlinking a Google account so newly
   *  granted calendars appear (and removed ones disappear) without a full app reload. */
  reload: () => void;
}

export function useCalendarPrefs(api: CalendarApi | null): UseCalendarPrefs {
  const [calendars, setCalendars] = useState<UserCalendarDTO[]>([]);
  // Bumped by reload() to re-run the load effect. The initial fetch keyed only on `api` (memoized for
  // the app's lifetime) never re-fired on account changes, so a freshly connected account's calendars
  // stayed invisible until reload — this nonce is the explicit refresh signal.
  const [reloadNonce, setReloadNonce] = useState(0);
  const overrides = useRef<Record<string, boolean>>({});
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const reload = useCallback(() => setReloadNonce((n) => n + 1), []);

  useEffect(() => {
    if (!api) {
      setCalendars([]);
      return;
    }
    let cancelled = false;
    void Promise.all([api.getCalendars(), api.getCalendarPrefs()])
      .then(([list, prefs]) => {
        if (cancelled) return;
        overrides.current = { ...prefs.overrides };
        setCalendars(list.calendars);
      })
      .catch(() => {
        /* offline / unauthorized → nothing to configure */
      });
    return () => {
      cancelled = true;
    };
  }, [api, reloadNonce]);

  useEffect(
    () => () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    },
    [],
  );

  const setEnabled = useCallback(
    (accountId: string, calendarId: string, enabled: boolean) => {
      overrides.current = { ...overrides.current, [calendarPrefKey(accountId, calendarId)]: enabled };
      setCalendars((cs) =>
        cs.map((c) => (c.accountId === accountId && c.calendarId === calendarId ? { ...c, enabled } : c)),
      );
      if (api) {
        if (saveTimer.current) clearTimeout(saveTimer.current);
        const snapshot = { overrides: overrides.current };
        saveTimer.current = setTimeout(() => {
          void api.putCalendarPrefs(snapshot).catch(() => {
            /* offline → next successful load reconciles */
          });
        }, SAVE_DEBOUNCE_MS);
      }
    },
    [api],
  );

  return { calendars, setEnabled, reload };
}
